import { computed, ref } from 'vue';
import gql from 'graphql-tag';
import { useQuery } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import useEquipment from './useEquipment';
import usePermission from '~/composables/usePermission';
import useSchedule from '~/composables/useSchedule';
import { isTenantFeatureActive } from '~/composables/useTenant';

// GraphQL query to fetch feature flags
const GET_FEATURE_FLAGS = gql`
query Features {
  features {
    label
    options
  }
}
`;

export default function useNavigationDrawer() {
  const { canUser, canView } = usePermission();
  const { useAreasAndWorkCenters } = useEquipment();
  const { useAvailabilityPeriods } = useSchedule();
  const features = ref({});
  const route = useRoute();
  /**
   * The raw dashboard list with their parent categories.
   * Includes dashboards before applying permission filters and without translations.
   */
  const rawDashboardList = computed(() => [
    {
      category: 'navigation.drawer.dashboard.label.machines',
      tooltip: 'navigation.drawer.dashboard.tooltip.machines',
      icon: 'add_chart no-fill',
      items: [
        {
          title: 'navigation.drawer.dashboard.label.machineTracking',
          tooltip: 'navigation.drawer.dashboard.tooltip.machineTracking',
          dashboardId: '1005',
          url: '/dashboard/1005',
        },
        {
          title: 'navigation.drawer.dashboard.label.eventCategorization',
          tooltip: 'navigation.drawer.dashboard.tooltip.eventCategorization',
          url: '/categorization',
          resource: 'Events',
        },
        {
          title: 'navigation.drawer.dashboard.label.production',
          tooltip: 'navigation.drawer.dashboard.tooltip.production',
          dashboardId: '1006',
          url: '/dashboard/1006',
        },
        {
          title: 'navigation.drawer.dashboard.label.losses',
          tooltip: 'navigation.drawer.dashboard.tooltip.losses',
          dashboardId: '1004',
          url: '/dashboard/1004',
        },
        {
          title: 'navigation.drawer.dashboard.label.monitoring',
          tooltip: 'navigation.drawer.dashboard.tooltip.monitoring',
          dashboardId: '1008',
          url: '/dashboard/1008',
        },
        {
          title: 'navigation.drawer.dashboard.label.cycles',
          tooltip: 'navigation.drawer.dashboard.tooltip.cycles',
          dashboardId: '1010',
          url: '/dashboard/1010',
        },
        {
          title: 'navigation.drawer.dashboard.label.events',
          tooltip: 'navigation.drawer.dashboard.tooltip.events',
          url: '/event',
          resource: 'Events',
        },
        {
          title: 'navigation.drawer.dashboard.label.synthesisKPI',
          tooltip: 'navigation.drawer.dashboard.tooltip.synthesisKPI',
          dashboardId: '1007',
          url: '/dashboard/1007',
        },
      ],
    },
    {
      category: 'navigation.drawer.dashboard.label.productionMonitoring',
      tooltip: 'navigation.drawer.dashboard.tooltip.productionMonitoring',
      icon: 'monitoring no-fill',
      items: [
        {
          title: 'navigation.drawer.dashboard.label.globalKPI',
          tooltip: 'navigation.drawer.dashboard.tooltip.globalKPI',
          dashboardId: '1011',
          url: '/dashboard/1011',
        },
        {
          title: 'navigation.drawer.dashboard.label.status',
          tooltip: 'navigation.drawer.dashboard.tooltip.status',
          dashboardId: '1003',
          url: '/dashboard/1003',
        },
        {
          title: 'navigation.drawer.dashboard.label.globalUtilization',
          tooltip: 'navigation.drawer.dashboard.tooltip.globalUtilization',
          dashboardId: '1015',
          url: '/dashboard/1015',
        },
        {
          title: 'navigation.drawer.dashboard.label.globalPareto',
          tooltip: 'navigation.drawer.dashboard.tooltip.globalPareto',
          dashboardId: '1016',
          url: '/dashboard/1016',
        },
        {
          title: 'navigation.drawer.dashboard.label.downtimesEvolution',
          tooltip: 'navigation.drawer.dashboard.tooltip.downtimesEvolution',
          dashboardId: '1019',
          url: '/dashboard/1019',
        },
        {
          title: 'navigation.drawer.dashboard.label.globalLosses',
          tooltip: 'navigation.drawer.dashboard.tooltip.globalLosses',
          dashboardId: '1012',
          url: '/dashboard/1012',
        },
        {
          title: 'navigation.drawer.dashboard.label.performance',
          tooltip: 'navigation.drawer.dashboard.tooltip.performance',
          dashboardId: '1009',
          url: '/dashboard/1009',
        },
        {
          title: 'navigation.drawer.dashboard.label.performanceIndicators',
          tooltip: 'navigation.drawer.dashboard.tooltip.performanceIndicators',
          dashboardId: '1020',
          url: '/dashboard/1020',
        },
      ],
    },
    {
      category: 'navigation.drawer.dashboard.label.tables',
      tooltip: 'navigation.drawer.dashboard.tooltip.tables',
      icon: 'toolbar no-fill',
      items: [
        {
          title: 'navigation.drawer.dashboard.label.abnormalVibrations',
          tooltip: 'navigation.drawer.dashboard.tooltip.abnormalVibrations',
          dashboardId: '1014',
          url: '/dashboard/1014',
        },
        {
          title: 'navigation.drawer.dashboard.label.eventsStopsTables',
          tooltip: 'navigation.drawer.dashboard.tooltip.eventsStopsTables',
          dashboardId: '1017',
          url: '/dashboard/1017',
        },
        {
          title: 'navigation.drawer.dashboard.label.supervisionTable',
          tooltip: 'navigation.drawer.dashboard.tooltip.supervisionTable',
          dashboardId: '1018',
          url: '/dashboard/1018',
        },
      ],
    },
    {
      category: 'navigation.drawer.dashboard.label.planning',
      tooltip: 'navigation.drawer.dashboard.tooltip.planning',
      icon: 'list_alt_check no-fill',
      items: [
        {
          title: 'navigation.drawer.dashboard.label.jobs',
          tooltip: 'navigation.drawer.dashboard.tooltip.jobs',
          url: '/planning',
          resource: 'Planning',
        },
        {
          title: 'navigation.drawer.dashboard.label.toPlan',
          tooltip: 'navigation.drawer.dashboard.tooltip.toPlan',
          url: '/planning-to-plan',
          resource: 'Planning',
        },
        {
          title: 'navigation.drawer.dashboard.label.launched',
          tooltip: 'navigation.drawer.dashboard.tooltip.launched',
          url: '/planning-launched',
          resource: 'Planning',
        },
        {
          title: 'navigation.drawer.dashboard.label.done',
          tooltip: 'navigation.drawer.dashboard.tooltip.done',
          url: '/planning-done',
          resource: 'Planning',
        },
      ],
    },
  ]);

  /**
   * Filters the dashboard list based on allowed dashboards.
   * @param {Array} allowedDashboards - The dashboards user has access to.
   * @returns {Array} - Filtered dashboard list.
   */
  function getFilteredDashboardList(allowedDashboards) {
    return rawDashboardList.value.map((category) => {
      const items = category.items.reduce(
        (targetItems, item) => {
          if (!item?.dashboardId && item?.resource) {
            // Check if resource is visible in UI (View bit = 16)
            const isVisible = canView(item.resource);
            if (isVisible) {
              targetItems.push(item);
              return targetItems;
            }
          }
          const allowedDashboard = allowedDashboards
            .find((dashboard) => dashboard.id === item.dashboardId);
          if (allowedDashboard) {
            targetItems.push(item);
          }

          return targetItems;
        },
        [],
      );
      return {
        ...category,
        items,
      };
    }).filter((category) => category.items.length > 0);
  }
  const { t } = useI18n();
  /**
   * The raw settings list with their parent categories.
   */
  const rawSettingsItems = computed(() => [
    {
      title: t('user.account'),
      value: 'profile',
      props: {
        prependIcon: 'account_circle no-fill',
      },
      children: [
        {
          title: t('user.profile'),
          props: {
            exact: true,
            to: { name: 'myProfile' },
          },
        },
        {
          title: t('menu.secondary.myPreferences'),
          props: {
            exact: true,
            to: { name: 'myProfile.preferences' },
          },
        },
        {
          title: t('user.access'),
          props: {
            exact: true,
            to: { name: 'myProfile.access' },
          },
        },
      ],
    },
    {
      title: t('navigation.drawer.settings.label.organization'),
      value: 'organization',
      props: {
        class: 'red-text',
        prependIcon: 'folder_managed no-fill',
      },
      children: [
        {
          title: t('navigation.drawer.settings.label.devices'),
          props: {
            to: { name: 'settings.devices' },
            tooltip: t('navigation.drawer.settings.tooltip.devices'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.users'),
          props: {
            to: { name: 'settings.users' },
            tooltip: t('navigation.drawer.settings.tooltip.staff'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.languages'),
          props: {
            to: { name: 'settings.languages' },
            tooltip: t('navigation.drawer.settings.tooltip.languages'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.personalization'),
          props: {
            to: { name: 'settings.personalization.themes' },
            tooltip: t('navigation.drawer.settings.tooltip.interface'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.security'),
          props: {
            to: { name: 'settings.security' },
            tooltip: t('navigation.drawer.settings.tooltip.security'),
          },
        },
        /*
         * Next to Security rather than inside it: an administrator wiring up their IT looks for
         * "integration", not "security". Both tabs of the section sit behind a tenant flag
         * (`externalApi` for the API tokens, `sso` for the directory), and the filter below drops
         * an entry whose route sits behind a lowered one. The entry therefore opens on the API
         * tokens when they are open, on SSO otherwise, and vanishes when neither is.
         */
        {
          title: t('navigation.drawer.settings.label.integrations'),
          props: {
            to: { name: isTenantFeatureActive('externalApi') ? 'settings.apiTokens' : 'settings.sso' },
            tooltip: t('navigation.drawer.settings.tooltip.integrations'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.newFeatures'),
          props: {
            to: { name: 'settings.newFeatures' },
            tooltip: t('navigation.drawer.settings.tooltip.newFeatures'),
          },
        },
      ],
    },
    {
      title: t('navigation.drawer.settings.label.operations'),
      value: 'settings',
      props: {
        prependIcon: 'factory no-fill',
      },
      children: [
        {
          title: t('navigation.drawer.settings.label.equipment'),
          props: {
            to: { name: useAreasAndWorkCenters.value ? 'settings.equipments' : 'settings.factories' },
            tooltip: t('navigation.drawer.settings.tooltip.equipment'),
          },
        },
        {
          // If the availability periods feature is enabled,
          // we want to navigate to the schedules page instead of opening times
          title: useAvailabilityPeriods.value ? t('navigation.drawer.settings.label.schedulesAndCalendars') : t('navigation.drawer.settings.label.time'),
          props: {
            to: {
              name: useAvailabilityPeriods.value ? 'settings.schedules' : 'settings.openingTimes',
            },
            tooltip: useAvailabilityPeriods.value
              ? t('navigation.drawer.settings.tooltip.schedulesAndCalendars')
              : t('navigation.drawer.settings.tooltip.time'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.downtimeCauses'),
          props: {
            to: { name: 'settings.downtimeCauses' },
            tooltip: useAvailabilityPeriods.value
              ? t('navigation.drawer.settings.tooltip.downtimeCausesAndRecurringEvents')
              : t('navigation.drawer.settings.tooltip.downtimeCauses'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.machinesObjectives'),
          props: {
            to: { name: 'settings.machinesObjectives' },
            tooltip: t('navigation.drawer.settings.tooltip.machinesObjectives'),
          },
        },
        {
          title: t('navigation.drawer.dashboard.label.production'),
          props: {
            to: { name: 'settings.products' },
            tooltip: t('navigation.drawer.settings.tooltip.products'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.alerts'),
          props: {
            to: { name: 'settings.alerts' },
            tooltip: t('navigation.drawer.settings.tooltip.alerts'),
          },
        },
        {
          title: t('navigation.drawer.settings.label.exports'),
          props: {
            to: { name: 'settings.dataExport' },
            tooltip: t('navigation.drawer.settings.tooltip.exports'),
          },
        },
      ],
    },
  ]);
  const router = useRouter();
  /**
   * Filters the settings list by user permissions and feature flags.
   */
  const navigationDrawerSettingsItems = computed(
    () => rawSettingsItems.value.map((item) => {
      // Applies user permissions to the settings items
      const filteredChildren = Array.isArray(item.children)
        ? item.children.filter((menuItem) => {
          if (!menuItem?.props?.to) {
            return true;
          }
          // Resolve the route via vue-router
          const resolvedRoute = router.resolve(menuItem?.props?.to);
          if (!resolvedRoute) {
            return true;
          }

          // Finding the last matched route meta, the one permissionNavigationGuard
          // checks when the user navigates
          const matchedMeta = resolvedRoute.matched[resolvedRoute.matched.length - 1]?.meta;
          const resource = matchedMeta?.resource;
          if (!resource) {
            return true;
          }

          // Same rule as permissionNavigationGuard: View UI (16) by default, but
          // the action the route requires when it declares one. Checking only the
          // View bit here would keep linking to pages the guard redirects to
          // "forbidden" (e.g. the ISA-95 equipments routes, which require Update
          // on WorkCenters).
          const isVisible = canUser(matchedMeta.actionPermission ?? 16, resource);
          // Même règle que la barre d'onglets de PageSettings : une destination derrière un
          // drapeau de tenant n'existe pas tant qu'il est baissé. Sans cela le menu offrirait
          // un lien que le `beforeEnter` de la route renvoie aussitôt ailleurs — et l'onglet,
          // lui, disparaissait bien. Une section d'un seul écran n'a plus de barre pour
          // rattraper l'oubli.
          if (matchedMeta.featureFlag && !isTenantFeatureActive(matchedMeta.featureFlag)) {
            return false;
          }

          // Must just be "visible"
          if (features.value[resolvedRoute.meta.resource.toUpperCase()] === undefined) {
            return isVisible;
          }

          // Must be "visible" and the feature flag must be active
          return isVisible && features.value[resolvedRoute.meta.resource.toUpperCase()];
        }).map((menuItem) => {
          // Keep item as-is if it doesn't navigate anywhere
          if (!menuItem?.props?.to) {
            return menuItem;
          }
          // Resolve the target route safely
          let resolvedRoute = null;
          try {
            resolvedRoute = router.resolve(menuItem.props.to);
          } catch (e) {
            resolvedRoute = null;
          }
          // Check if the target route is in the same tab group as the current route
          // If it is, set the active prop to true
          const sameTabGroup = !!(resolvedRoute?.meta?.tabGroup
            && route.meta?.tabGroup
            && resolvedRoute.meta.tabGroup === route.meta.tabGroup);
          return {
            ...menuItem,
            props: {
              ...(menuItem.props || {}),
              active: sameTabGroup || undefined,
            },
          };
        })
        : [];
      return {
        ...item,
        children: filteredChildren,
      };
    }).filter(
      // Removing empty categories
      (item) => item.children.length > 0,
    ),
  );

  // Fetches feature flags
  const {
    onResult: onFeatureFlagsResult,
  } = useQuery(
    GET_FEATURE_FLAGS,
    {},
    {
      fetchPolicy: 'no-cache',
      manual: true,
    },
  );

  onFeatureFlagsResult((result) => {
    if (!result.loading) {
      const featureData = result?.data?.features;
      if (Array.isArray(featureData)) {
        featureData.forEach((feature) => {
          let { options } = feature;
          if (typeof options === 'string') {
            try {
              options = JSON.parse(options);
            } catch (e) {
              console.error(`Error parsing options for feature ${feature.label}:`, e);
              return;
            }
          }
          features.value[feature.label] = options?.active ?? false;
        });
      }
    }
  });

  return {
    getFilteredDashboardList,
    navigationDrawerSettingsItems,
    rawDashboardList,
  };
}
